import {
  faAngleDown,
  faAngleUp,
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowUpRightAndArrowDownLeftFromCenter,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import useTracking from '../../hooks/useTracking';
import { BLACK2, BORDER_GRAY, EXOS_GRAY } from '../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import Avatar, { AvatarTag } from '../reusable/Avatar';
import { ButtonSize } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import { Link, LinkDecoration } from '../reusable/Link';
import Text, { TextColor, TextKind } from '../reusable/Text';
import { ChatSize, useChatContext } from './Context';
import { HeaderContent } from './util';

const StyledChatHeader = styled.header`
  display: flex;
  border-bottom: 1px solid ${BORDER_GRAY};
  justify-content: space-between;
  flex-direction: column;
`;

const StyledChatHeaderTextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  overflow: hidden;

  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledChatHeaderActionContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledMemberAndActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 8px;
`;

const PrimaryCoachBanner = styled.div`
  background-color: ${EXOS_GRAY};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px 8px 0 0;
`;

interface HeaderProps {
  content: HeaderContent;
  closeable?: boolean;
  showBanner?: boolean;
  resetSelectedMembers?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  content,
  closeable,
  showBanner = false,
  resetSelectedMembers,
}) => {
  const { closeChat, chatSize, setChatSize, chatId, member } = useChatContext();
  const { track } = useTracking();
  const handleOpenChat = () => {
    if (chatSize === ChatSize.Minimized) {
      setChatSize(ChatSize.Open);
      track(EventNames.CaretClick, {
        event_category: EventCategories.Chat,
        event_subcategory: EventSubCategories.Expand,
        chat_id: chatId,
      });
      return;
    }
    setChatSize(ChatSize.Minimized);
    track(EventNames.CaretClick, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Collapse,
      chat_id: chatId,
    });
  };

  const maximizeChat = () => {
    if (chatSize === ChatSize.Maximized) {
      setChatSize(ChatSize.Open);
      track(EventNames.ExpansionClick, {
        event_category: EventCategories.Chat,
        event_subcategory: EventSubCategories.Collapse,
        chat_id: chatId,
      });
      return;
    }

    setChatSize(ChatSize.Maximized);
    track(EventNames.ExpansionClick, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Expand,
      chat_id: chatId,
    });
  };

  const handleCloseChat = () => {
    resetSelectedMembers?.();
    closeChat();
    track(EventNames.CloseClick, {
      event_category: EventCategories.Chat,
      chat_id: chatId,
    });
  };

  const hideAvatar = !content.avatarMember && content.hideEmptyElements;
  const hideSubtitle = !content.subtitle && content.hideEmptyElements;

  const headerContent = (
    <>
      {!hideAvatar && (
        <Avatar
          user={content.avatarMember}
          size={40}
          tag={
            content.avatarMember?.isStarred ? AvatarTag.Star : AvatarTag.None
          }
        />
      )}
      <StyledChatHeaderTextContent>
        <Text kind={TextKind.LabelL} testId="chat-header-member-title">
          {content.title}
        </Text>
        {!hideSubtitle && (
          <Text
            kind={TextKind.BodyS}
            color={TextColor.Secondary}
            testId="chat-header-member-subtitle"
          >
            {content.subtitle || '---'}
          </Text>
        )}
      </StyledChatHeaderTextContent>
    </>
  );

  return (
    <StyledChatHeader data-testid="chat-header">
      {chatSize !== ChatSize.Minimized && showBanner && (
        <PrimaryCoachBanner>
          <FontAwesomeIcon icon={faSquare} size="sm" color={BLACK2} />
          <Text>{`Primary Coach: ${member?.fitCoach?.firstName} ${member?.fitCoach?.lastName}`}</Text>
        </PrimaryCoachBanner>
      )}
      <StyledMemberAndActionsContainer>
        {member?.id ? (
          <Link
            to={`/app/members/${member?.id}`}
            decoration={LinkDecoration.Hover}
            trackingData={{
              eventName: EventNames.MemberClick,
              event_label: 'Chat Header Member Link',
              event_category: EventCategories.Chat,
              chat_id: chatId,
              member_id: member?.id,
            }}
          >
            {headerContent}
          </Link>
        ) : (
          headerContent
        )}

        <StyledChatHeaderActionContent>
          {chatSize !== ChatSize.Minimized && (
            <IconButton
              testId="chat-header-maximizing-button"
              size={ButtonSize.Small}
              icon={
                <FontAwesomeIcon
                  data-testid="chat-header-maximizing-button-icon"
                  icon={
                    chatSize === ChatSize.Maximized
                      ? faArrowDownLeftAndArrowUpRightToCenter
                      : faArrowUpRightAndArrowDownLeftFromCenter
                  }
                  fontSize={16}
                />
              }
              onClick={maximizeChat}
            />
          )}
          <IconButton
            testId="chat-header-expansion-button"
            size={ButtonSize.Small}
            icon={
              <FontAwesomeIcon
                data-testid="chat-header-expansion-button-icon"
                icon={chatSize === ChatSize.Minimized ? faAngleUp : faAngleDown}
                fontSize={20}
              />
            }
            onClick={handleOpenChat}
          />
          {closeable && (
            <IconButton
              testId="chat-header-close-button"
              size={ButtonSize.Small}
              icon={<FontAwesomeIcon icon={faTimes} fontSize={20} />}
              onClick={handleCloseChat}
              ariaLabel="close chat"
            />
          )}
        </StyledChatHeaderActionContent>
      </StyledMemberAndActionsContainer>
    </StyledChatHeader>
  );
};
